// GitHub backup credentials + last-push state for the git-canonical
// durability layer. THREE different concerns now touch GitHub PATs in
// this app — keep them distinct:
//
//   keyStore.babyai            — BabyAI HF token (model calls)
//   useGitHubStore (existing)  — PAT for per-channel sync (sharing
//                                ONE channel JSON to a chosen repo
//                                path so teammates see what you see)
//   useBackupStore (this file) — PAT for whole-Playground backup
//                                (one personal repo holding the full
//                                git-canonical snapshot — channels +
//                                library + templates + prefs)
//
// The token here is a fine-grained PAT scoped to a single repo with
// contents:read+write. Stored in localStorage like the other keys —
// browser-local, never goes through the worker, sent only as
// Authorization: Bearer on direct calls to api.github.com.

import { create } from "zustand";

const STORAGE_TOKEN     = "playground.backup.token";
const STORAGE_REPO      = "playground.backup.repo";
const STORAGE_LAST_OID  = "playground.backup.last_pushed_oid";
const STORAGE_LAST_AT   = "playground.backup.last_pushed_at";
const STORAGE_AUTO_PUSH = "playground.backup.auto_push";

type BackupState = {
  /** Fine-grained PAT. Null until the user enters one. */
  token: string | null;
  /** "<owner>/<repo>" — the destination for backups. */
  repo: string | null;
  /** Most recent commit OID we successfully pushed. Used to surface
   *  "up to date" vs "drift" in the UI. Null = never pushed. */
  lastPushedOid: string | null;
  /** When the last successful push completed, ms epoch. */
  lastPushedAt: number | null;
  /** Opt-in flag — when true, every local git-store commit schedules
   *  a debounced push. Default off. Requires token + repo to be set
   *  for any push to actually fire. */
  autoPush: boolean;
  setToken: (token: string | null) => void;
  setRepo: (repo: string | null) => void;
  setLastPushed: (oid: string, at: number) => void;
  setAutoPush: (on: boolean) => void;
  clear: () => void;
};

function loadStr(key: string): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(key);
}

function loadNum(key: string): number | null {
  const raw = loadStr(key);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export const useBackupStore = create<BackupState>((set) => ({
  token: loadStr(STORAGE_TOKEN),
  repo: loadStr(STORAGE_REPO),
  lastPushedOid: loadStr(STORAGE_LAST_OID),
  lastPushedAt: loadNum(STORAGE_LAST_AT),
  autoPush: loadStr(STORAGE_AUTO_PUSH) === "true",
  setToken: (token) => {
    if (typeof localStorage !== "undefined") {
      if (token) localStorage.setItem(STORAGE_TOKEN, token);
      else localStorage.removeItem(STORAGE_TOKEN);
    }
    set({ token });
  },
  setRepo: (repo) => {
    if (typeof localStorage !== "undefined") {
      if (repo) localStorage.setItem(STORAGE_REPO, repo);
      else localStorage.removeItem(STORAGE_REPO);
    }
    set({ repo });
  },
  setLastPushed: (oid, at) => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_LAST_OID, oid);
      localStorage.setItem(STORAGE_LAST_AT, String(at));
    }
    set({ lastPushedOid: oid, lastPushedAt: at });
  },
  setAutoPush: (on) => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_AUTO_PUSH, on ? "true" : "false");
    }
    set({ autoPush: on });
  },
  clear: () => {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(STORAGE_TOKEN);
      localStorage.removeItem(STORAGE_REPO);
      localStorage.removeItem(STORAGE_LAST_OID);
      localStorage.removeItem(STORAGE_LAST_AT);
      localStorage.removeItem(STORAGE_AUTO_PUSH);
    }
    set({ token: null, repo: null, lastPushedOid: null, lastPushedAt: null, autoPush: false });
  },
}));
